/**
 * Field Map – Mobile Portal
 *
 * Purpose: Read-only map showing mobile units, NamPost offices, ATMs across Namibia.
 * Consistent with Ketchup Portal NamibiaMap (same markers, bounds, types).
 * Location: mobile-portal/src/components/map/FieldMap.tsx
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Badge } from '@smartpay/ui';
import { Search, MapPin, Building, Truck } from 'lucide-react';
import type { MapLocation, LocationType, LocationStatus } from '@smartpay/api-client/ketchup';
import { getLocationTypeColor } from '../../constants/channelColors';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const NAMIBIA_BOUNDS: L.LatLngBoundsLiteral = [
  [-28.0, 11.0],
  [-16.0, 25.0],
];
const DEFAULT_CENTER: [number, number] = [-22.5609, 17.0658];

const locationTypeLabels: Record<string, string> = {
  'agent-pos': 'Agent POS',
  'nampost-office': 'NamPost Office',
  'merchant-store': 'Merchant Store',
  atm: 'ATM',
  warehouse: 'Warehouse',
  mobile_unit: 'Mobile Unit',
};

function createCustomIcon(type: LocationType | string, status: LocationStatus): L.DivIcon {
  const statusColor: Record<string, string> = {
    active: '#10b981',
    inactive: '#6b7280',
    maintenance: '#f59e0b',
    offline: '#ef4444',
  };
  const iconHtml = `
    <div style="
      position: relative;
      width: 16px;
      height: 16px;
      background-color: ${getLocationTypeColor(type)};
      border: 1px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    ">
      <div style="
        width: 4px;
        height: 4px;
        background-color: ${statusColor[status] ?? '#6b7280'};
        border-radius: 50%;
        position: absolute;
        bottom: -1px;
        right: -1px;
        border: 1px solid white;
      "></div>
    </div>
  `;
  return L.divIcon({
    html: iconHtml,
    className: 'custom-marker-icon',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  });
}

export interface FieldMapProps {
  locations?: MapLocation[];
  onLocationClick?: (location: MapLocation) => void;
}

export function FieldMap({ locations = [], onLocationClick }: FieldMapProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredLocations = useMemo(() => {
    return locations.filter((location) => {
      const matchesSearch =
        searchQuery === '' ||
        location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.address?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = selectedType === 'all' || location.type === selectedType;
      return matchesSearch && matchesType;
    });
  }, [locations, searchQuery, selectedType]);

  const stats = useMemo(
    () => ({
      total: locations.length,
      mobileUnits: locations.filter((l) => l.type === 'mobile_unit').length,
      nampost: locations.filter((l) => l.type === 'nampost-office').length,
      atms: locations.filter((l) => l.type === 'atm').length,
    }),
    [locations]
  );

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Mobile Units, NamPost & ATMs
            </CardTitle>
            <CardDescription>
              Mobile units, NamPost offices, and ATM coordinates
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{stats.total} locations</Badge>
            <Badge className="bg-violet-600">{stats.mobileUnits} units</Badge>
            <Badge variant="secondary">{stats.nampost} NamPost</Badge>
            <Badge variant="secondary">{stats.atms} ATMs</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full sm:w-[180px]"
          >
            <option value="all">All types</option>
            <option value="mobile_unit">Mobile Units</option>
            <option value="nampost-office">NamPost</option>
            <option value="atm">ATMs</option>
            <option value="warehouse">Warehouse</option>
          </select>
        </div>

        <div className="rounded-lg overflow-hidden border h-[calc(100vh-18rem)] min-h-[400px] w-full">
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={6}
            minZoom={5}
            maxZoom={18}
            bounds={NAMIBIA_BOUNDS}
            maxBounds={NAMIBIA_BOUNDS}
            maxBoundsViscosity={1}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filteredLocations.map((location) => (
              <Marker
                key={location.id}
                position={[location.latitude, location.longitude]}
                icon={createCustomIcon(location.type, location.status)}
                eventHandlers={{
                  click: () => onLocationClick?.(location),
                }}
              >
                <Popup>
                  <div className="space-y-2 min-w-[200px]">
                    <h3 className="font-semibold">{location.name}</h3>
                    <Badge variant="outline">{locationTypeLabels[location.type] ?? location.type}</Badge>
                    {location.address && <p className="text-sm">{location.address}</p>}
                    <p className="text-sm text-muted-foreground">Region: {location.region}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="flex flex-wrap gap-4 p-4 border rounded-lg">
          {(['mobile_unit', 'nampost-office', 'atm'] as const).map((type) => (
            <div key={type} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: getLocationTypeColor(type) }}
              />
              <span className="text-sm">{locationTypeLabels[type] ?? type}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
